/**
 * motion-controller.ts - Controlador Mestre de Movimento e Cinemática de Bancada
 * Baseado na Seção 11 do Guia Profissional de Animações Realistas
 * Filosofia Fábio Akita: Módulos Profundos, Zero Mocks e Controle Determinístico.
 */

import {
  CVT_PARTS_3D_METROLOGY,
  Cvt3DExplosionEngine,
  type Part3DConfig,
} from "../../components/cvt-3d-model.js";
import {
  CVT_OFFICIAL_PHASES,
  CVT_TIMELINE_TOTAL_DURATION_SEC,
  CvtGsapAnimator,
  type CvtPhaseInfo,
} from "../../components/cvt-gsap-animator.js";
import type {
  IMotionController,
  MechanicalStepEvent,
  MotionControllerConfig,
  MotionState,
} from "./motion-types.js";
import { Adv150SoundEngine } from "./sound-engine.js";

export class Adv150MotionController implements IMotionController {
  private state: MotionState = "ready";
  private progress = 0.0; // 0.0 a 1.0
  private speed = 1.0;
  private readonly animator: CvtGsapAnimator;
  private readonly explosionEngine: Cvt3DExplosionEngine;
  private readonly soundEngine: Adv150SoundEngine;
  private lastPhaseNumber = 1;
  private animFrameId: number | null = null;
  private lastTimestamp = 0;

  // Callbacks
  private readonly onStateChange?: (state: MotionState) => void;
  private readonly onStepChange?: (event: MechanicalStepEvent) => void;
  private readonly onProgress?: (progress: number) => void;

  constructor(config: MotionControllerConfig = {}) {
    this.speed = config.initialSpeed ?? 1.0;
    this.animator = new CvtGsapAnimator(CVT_OFFICIAL_PHASES, this.speed, 0);
    this.explosionEngine = new Cvt3DExplosionEngine(CVT_PARTS_3D_METROLOGY);
    this.soundEngine = new Adv150SoundEngine(config.soundOptions ?? {});

    this.onStateChange = config.onStateChange;
    this.onStepChange = config.onStepChange;
    this.onProgress = config.onProgress;

    // Dispara evento inicial da fase 1
    this.notifyStepChange(this.animator.getPhaseAt(0));
  }

  public getState(): MotionState {
    return this.state;
  }

  public getProgress(): number {
    return this.progress;
  }

  public getSpeed(): number {
    return this.speed;
  }

  public isXRayActive(): boolean {
    return this.explosionEngine.isXRay();
  }

  public getSoundEngine(): Adv150SoundEngine {
    return this.soundEngine;
  }

  public getExplosionEngine(): Cvt3DExplosionEngine {
    return this.explosionEngine;
  }

  public getParts(): ReadonlyArray<Part3DConfig> {
    return this.explosionEngine.getAllParts();
  }

  public play(): void {
    if (this.state === "playing") return;

    this.soundEngine.ensureContext();
    this.animator.play();
    this.setState("playing");
    this.lastTimestamp = 0;
    this.startLoop();
  }

  public pause(): void {
    if (this.state === "paused") return;

    this.animator.pause();
    this.soundEngine.stopAll();
    this.stopLoop();
    this.setState("paused");
  }

  public togglePlay(): boolean {
    if (this.state === "playing") {
      this.pause();
      return false;
    }
    this.play();
    return true;
  }

  public seek(normalizedProgress: number): void {
    const clamped = Math.max(0, Math.min(1, normalizedProgress));
    this.progress = clamped;
    const targetSeconds = clamped * CVT_TIMELINE_TOTAL_DURATION_SEC;

    this.animator.setCurrentTime(targetSeconds);
    this.explosionEngine.setExplosionProgress(clamped);

    const phase = this.animator.getPhaseAt(targetSeconds);
    this.checkPhaseTransition(phase);

    if (this.onProgress) {
      this.onProgress(this.progress);
    }
  }

  public seekToPhase(phaseNumber: number): void {
    const targetSeconds = this.animator.seekToPhase(phaseNumber);
    const normalized = targetSeconds / CVT_TIMELINE_TOTAL_DURATION_SEC;
    this.seek(normalized);
  }

  public setSpeed(multiplier: number): number {
    this.speed = this.animator.setSpeed(multiplier);
    return this.speed;
  }

  public toggleXRay(): boolean {
    return this.explosionEngine.toggleXRay();
  }

  public reset(): void {
    this.pause();
    this.seek(0);
    this.soundEngine.stopAll();
  }

  public dispose(): void {
    this.stopLoop();
    this.soundEngine.dispose();
    this.setState("unloaded");
  }

  public getCurrentPhase(): CvtPhaseInfo {
    return this.animator.getPhaseAt(this.animator.getCurrentTime());
  }

  public calculateAxialPositions(): Record<
    string,
    { z: number; opacity: number; wireframe: boolean }
  > {
    return this.explosionEngine.calculateAxialPositions();
  }

  // --- Métodos Internos de Ciclo de Vida e Sincronização ---

  private setState(newState: MotionState): void {
    if (this.state === newState) return;
    this.state = newState;
    if (this.onStateChange) {
      this.onStateChange(this.state);
    }
  }

  private startLoop(): void {
    if (typeof requestAnimationFrame === "undefined") return;

    const tick = (now: number) => {
      if (this.state !== "playing") return;

      if (this.lastTimestamp === 0) {
        this.lastTimestamp = now;
      }
      const deltaSec = (now - this.lastTimestamp) / 1000;
      this.lastTimestamp = now;

      const currentSec = this.animator.getCurrentTime();
      const newSec = currentSec + deltaSec * this.speed;

      if (newSec >= CVT_TIMELINE_TOTAL_DURATION_SEC) {
        this.seek(1.0);
        this.pause();
        return;
      }

      this.seek(newSec / CVT_TIMELINE_TOTAL_DURATION_SEC);
      this.animFrameId = requestAnimationFrame(tick);
    };

    this.animFrameId = requestAnimationFrame(tick);
  }

  private stopLoop(): void {
    if (this.animFrameId !== null && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.lastTimestamp = 0;
  }

  private checkPhaseTransition(phase: CvtPhaseInfo): void {
    if (phase.phaseNumber !== this.lastPhaseNumber) {
      this.lastPhaseNumber = phase.phaseNumber;
      this.notifyStepChange(phase);

      // Disparos sonoros determinísticos da mecânica oficial
      if (phase.phaseNumber === 5) {
        // Torque canônico de 59 N.m atingido! Dispara estalo no torquímetro
        this.soundEngine.playTorqueClick();
      } else if (phase.phaseNumber === 1 && this.state === "playing") {
        // Marcha lenta ativa a 1.700 RPM
        this.soundEngine.updateMotorAudio(1700, true);
      } else {
        this.soundEngine.stopAll();
      }
    }
  }

  private notifyStepChange(phase: CvtPhaseInfo): void {
    if (!this.onStepChange) return;

    let actionType: MechanicalStepEvent["action"] = "inspect";
    if (phase.phaseNumber === 1) actionType = "idle";
    else if (phase.phaseNumber === 2) actionType = "disassemble";
    else if (phase.phaseNumber === 4) actionType = "belt_sinking";
    else if (phase.phaseNumber === 5) actionType = "torque";

    const event: MechanicalStepEvent = {
      phaseNumber: phase.phaseNumber,
      id: phase.id,
      name: phase.name,
      action: actionType,
      torqueValueNm: phase.torqueSpecNm,
      criticalGotcha: phase.gotchaNote,
      telemetryTag: phase.telemetryTag,
      progressPercent: Number((this.progress * 100).toFixed(2)),
    };

    this.onStepChange(event);
  }
}
